import React, { useEffect, useState } from "react";
import { Dialog } from "@mui/material";
import { MdLocationOn } from "react-icons/md";
import { AiOutlineClose } from "react-icons/ai";

const clean6 = (s) => (s ?? "").toString().replace(/\D/g, "").slice(0, 6);

const PincodeToCity = () => {
  const [pincode, setPincode] = useState("");
  const [pinError, setPinError] = useState("");
  const [location, setLocation] = useState({
    latitude: null,
    longitude: null,
    city: "Fetching...",
    error: null,
  });
  const [isDialogOpen, setDialogOpen] = useState(false);

  // ---------------- Input & validation ----------------
  const handlePincodeChange = (e) => {
    const value = clean6(e.target.value);
    setPincode(value);

    if (value.length === 0) setPinError("");
    else if (value.length !== 6) setPinError("Pincode must be exactly 6 digits.");
    else setPinError("");
  };

  // ---------------- Reverse geocode: lat/lon -> city ----------------
  const fetchCityFromLatLng = async (lat, lon) => {
    try {
      const url =
        `https://nominatim.openstreetmap.org/reverse?format=json` +
        `&lat=${lat}&lon=${lon}&addressdetails=1&accept-language=en`;
      const response = await fetch(url, { headers: { Accept: "application/json" } });
      const data = await response.json();

      let city = "Unknown City";
      let apiPostcode = "";

      if (data && data.address) {
        apiPostcode = clean6(data.address.postcode);
        city =
          data.address.city ||
          data.address.town ||
          data.address.village ||
          data.address.suburb ||
          data.address.state_district ||
          data.address.state ||
          "Unknown City";
      }

      setPincode((prev) => {
        const prevClean = clean6(prev);
        const next =
          prevClean.length === 6
            ? prevClean
            : apiPostcode.length === 6
            ? apiPostcode
            : prevClean;

        if (next && next.length === 6) {
          try {
            localStorage.setItem("bp_pincode", next);
          } catch {}
        }
        return next;
      });

      const locObj = {
        latitude: Number(lat),
        longitude: Number(lon),
        city,
        error: null,
      };

      setLocation(locObj);

      try {
        localStorage.setItem("bp_location", JSON.stringify(locObj));
      } catch {}
    } catch {
      const locObj = {
        latitude: Number(lat) || null,
        longitude: Number(lon) || null,
        city: null,
        error: null,
      };
      setLocation(locObj);
      try {
        localStorage.setItem("bp_location", JSON.stringify(locObj));
      } catch {}
    }
  };

  // ---------------- Forward geocode: pincode -> lat/lon ----------------
  const fetchLatLngFromPincode = async () => {
    if (pincode.length !== 6) {
      setPinError("Pincode must be exactly 6 digits.");
      return;
    }

    try {
      const url =
        `https://nominatim.openstreetmap.org/search?format=json` +
        `&postalcode=${pincode}&countrycodes=IN&addressdetails=1&limit=5&accept-language=en`;

      const response = await fetch(url, { headers: { Accept: "application/json" } });
      const data = await response.json();

      if (Array.isArray(data) && data.length > 0) {
        const exact = data.find((r) => clean6(r?.address?.postcode) === pincode);
        const contains = data.find((r) => (r?.display_name || "").includes(pincode));
        const pick = exact || contains || data[0];
        const { lat, lon } = pick;

        try {
          localStorage.setItem("bp_pincode", pincode);
        } catch {}

        await fetchCityFromLatLng(lat, lon);
      } else {
        const locObj = {
          latitude: null,
          longitude: null,
          city: null,
          error: null,
        };
        setLocation(locObj);
        try {
          localStorage.setItem("bp_location", JSON.stringify(locObj));
        } catch {}
      }
    } catch {
      const locObj = {
        latitude: null,
        longitude: null,
        city: null,
        error: null,
      };
      setLocation(locObj);
      try {
        localStorage.setItem("bp_location", JSON.stringify(locObj));
      } catch {}
    }
  };

  // ---------------- Initial load: use saved data OR geolocation ----------------
  useEffect(() => {
    let hasSaved = false;

    try {
      const savedPin = localStorage.getItem("bp_pincode");
      const savedLoc = localStorage.getItem("bp_location");

      if (savedPin) {
        const cleaned = clean6(savedPin);
        if (cleaned) {
          setPincode(cleaned);
          hasSaved = true;
        }
      }

      if (savedLoc) {
        const parsed = JSON.parse(savedLoc);
        if (parsed && (parsed.city || parsed.latitude || parsed.longitude)) {
          setLocation((prev) => ({
            ...prev,
            ...parsed,
            city: parsed.city || prev.city || "Your location",
          }));
          hasSaved = true;
        }
      }
    } catch {
      // ignore storage errors
    }

    // Only ask for geolocation if NOTHING is saved
    if (!hasSaved && "geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          fetchCityFromLatLng(latitude, longitude);
        },
        () => {
          const locObj = {
            latitude: null,
            longitude: null,
            city: null,
            error: null,
          };
          setLocation(locObj);
          try {
            localStorage.setItem("bp_location", JSON.stringify(locObj));
          } catch {}
        }
      );
    } else if (!("geolocation" in navigator)) {
      setLocation((prev) => ({
        ...prev,
        error: "Geolocation is not supported by your browser.",
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---------------- UI ----------------
  return (
    <div className="flex flex-col items-center justify-center">
      <div className="max-w-lg cursor-pointer" onClick={() => setDialogOpen(true)}>
        <div className="flex gap-2 items-center justify-center">
          <MdLocationOn className="text-blue-600 text-4xl sm:text-3xl" />
          <div className="flex flex-col text-center">
            {location.error ? (
              <p className="text-red-600 text-sm mt-2">{location.error}</p>
            ) : (
              <div>
                <p className="text-sm font-semibold text-gray-800 m-0">
                  <span>Pincode:</span> {pincode || "Not entered"} <br />
                  <span>City:</span> {location.city || "Not set"}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Dialog for entering Pincode */}
      <Dialog
        open={isDialogOpen}
        onClose={() => setDialogOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <div className="relative p-8 bg-white rounded-lg shadow-2xl">
          <AiOutlineClose
            className="absolute top-4 right-4 text-gray-600 cursor-pointer text-2xl"
            onClick={() => setDialogOpen(false)}
          />
          <h3 className="text-xl font-semibold text-gray-800 mb-6">
            Choose your location
          </h3>
          <div className="mb-2">
            <input
              type="text"
              value={pincode}
              onChange={handlePincodeChange}
              maxLength={6}
              aria-invalid={pinError ? "true" : "false"}
              className={`w-full p-4 border-2 rounded-md focus:outline-none focus:ring-2 ${
                pinError
                  ? "border-red-500 focus:ring-red-500"
                  : "border-gray-300 focus:ring-blue-500"
              }`}
              placeholder="Enter Pincode"
            />
          </div>
          {pinError && (
            <p className="text-red-600 text-sm mb-4">{pinError}</p>
          )}

          <div className="flex justify-end space-x-4">
            <button
              onClick={async () => {
                if (pincode.length !== 6) {
                  setPinError("Pincode must be exactly 6 digits.");
                  return;
                }
                await fetchLatLngFromPincode();
                setDialogOpen(false);
              }}
              className="px-6 py-2 text-white bg-blue-600 hover:bg-blue-700 rounded-lg shadow-md transition duration-300 disabled:opacity-60"
            >
              Apply
            </button>
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default PincodeToCity;






































// import React, { useEffect, useState } from "react";
// import { Dialog } from "@mui/material";
// import { MdLocationOn } from "react-icons/md";
// import { AiOutlineClose } from "react-icons/ai";

// const clean6 = (s) => (s ?? "").toString().replace(/\D/g, "").slice(0, 6);

// const PincodeToCity = () => {
//   const [pincode, setPincode] = useState("");
//   const [pinError, setPinError] = useState("");
//   const [location, setLocation] = useState({
//     latitude: null,
//     longitude: null,
//     city: "Fetching...",
//     error: null,
//   });
//   const [isDialogOpen, setDialogOpen] = useState(false);

//   // keep input numeric and max 6 digits + live validation
//   const handlePincodeChange = (e) => {
//     const value = clean6(e.target.value);
//     setPincode(value);

//     if (value.length === 0) setPinError("");
//     else if (value.length !== 6) setPinError("Pincode must be exactly 6 digits.");
//     else setPinError("");
//   };

//   // Reverse geocode: lat/lon -> city (+ optional postcode)
//   const fetchCityFromLatLng = async (lat, lon) => {
//     try {
//       const url =
//         `https://nominatim.openstreetmap.org/reverse?format=json` +
//         `&lat=${lat}&lon=${lon}&addressdetails=1&accept-language=en`;
//       const response = await fetch(url, {
//         headers: { "Accept": "application/json" },
//       });
//       const data = await response.json();

//       let city = "Unknown City";
//       let apiPostcode = "";

//       if (data && data.address) {
//         apiPostcode = clean6(data.address.postcode);
//         city =
//           data.address.city ||
//           data.address.town ||
//           data.address.village ||
//           data.address.suburb ||
//           data.address.state_district ||
//           data.address.state ||
//           "Unknown City";
//       }

//       // ✅ Important: DO NOT override a valid user-entered PIN
//       // Only set from reverse geocode if we don't already have a valid 6-digit pin
//       setPincode((prev) => {
//         const prevClean = clean6(prev);
//         if (prevClean.length === 6) return prevClean; // keep user's pin
//         if (apiPostcode.length === 6) return apiPostcode; // use API only if we don't have one
//         return prevClean; // unchanged
//       });

//       setLocation({
//         latitude: Number(lat),
//         longitude: Number(lon),
//         city,
//         error: null,
//       });
//     } catch {
//       setLocation({
//         latitude: Number(lat) || null,
//         longitude: Number(lon) || null,
//         city: null,
//         // error: "Error fetching location data.",
//       });
//     }
//   };

//   // Forward geocode: pincode -> lat/lon (prefer exact postcode match)
//   const fetchLatLngFromPincode = async () => {
//     if (pincode.length !== 6) {
//       setPinError("Pincode must be exactly 6 digits.");
//       return;
//     }

//     try {
//       const url =
//         `https://nominatim.openstreetmap.org/search?format=json` +
//         `&postalcode=${pincode}&countrycodes=IN&addressdetails=1&limit=5&accept-language=en`;

//       const response = await fetch(url, {
//         headers: { "Accept": "application/json" },
//       });
//       const data = await response.json();

//       if (Array.isArray(data) && data.length > 0) {
//         // Prefer exact address.postcode match
//         const exact = data.find(
//           (r) => clean6(r?.address?.postcode) === pincode
//         );

//         // secondary: display_name containing the PIN
//         const contains = data.find(
//           (r) => (r?.display_name || "").includes(pincode)
//         );

//         const pick = exact || contains || data[0];
//         const { lat, lon } = pick;

//         await fetchCityFromLatLng(lat, lon);
//       } else {
//         setLocation({
//           latitude: null,
//           longitude: null,
//           city: null,
//           // error: "No location found for the provided pincode.",
//         });
//       }
//     } catch {
//       setLocation({
//         latitude: null,
//         longitude: null,
//         city: null,
//         // error: "Error fetching location data.",
//       });
//     }
//   };

//   // Get user's current location using Geolocation API
//   useEffect(() => {
//     if ("geolocation" in navigator) {
//       navigator.geolocation.getCurrentPosition(
//         (position) => {
//           const { latitude, longitude } = position.coords;
//           fetchCityFromLatLng(latitude, longitude);
//         },
//         () => {
//           setLocation({
//             latitude: null,
//             longitude: null,
//             city: null,
//             // error: "Unable to fetch your location. Please enter a pincode.",
//           });
//         }
//       );
//     } else {
//       setLocation({
//         latitude: null,
//         longitude: null,
//         city: null,
//         error: "Geolocation is not supported by your browser.",
//       });
//     }
//     // eslint-disable-next-line react-hooks/exhaustive-deps
//   }, []);

//   return (
//     <div className="flex flex-col items-center justify-center ">
//       <div
//         className="max-w-lg cursor-pointer"
//         onClick={() => setDialogOpen(true)}
//       >
//         <div className="flex gap-2 items-center justify-center ">
//           <MdLocationOn className="text-blue-600 text-4xl sm:text-3xl" />
//           <div className="flex flex-col text-center">
//             {location.error ? (
//               <p className="text-red-600 text-sm mt-2">{location.error}</p>
//             ) : (
//               <div className="">
//                 <p className="text-sm font-semibold text-gray-800 m-0">
//                   <span>Pincode:</span> {pincode || "Not entered"} <br />
//                   <span>City:</span> {location.city}
//                 </p>
               
//               </div>
//             )}
//           </div>
//         </div>
//       </div>

//       {/* Dialog for entering Pincode */}
//       <Dialog
//         open={isDialogOpen}
//         onClose={() => setDialogOpen(false)}
//         fullWidth
//         maxWidth="xs"
//       >
//         <div className="relative p-8 bg-white rounded-lg shadow-2xl">
//           <AiOutlineClose
//             className="absolute top-4 right-4 text-gray-600 cursor-pointer text-2xl"
//             onClick={() => setDialogOpen(false)}
//           />
//           <h3 className="text-xl font-semibold text-gray-800 mb-6">
//             Choose your location
//           </h3>
//           <div className="mb-2">
//             <input
//               type="text"
//               value={pincode}
//               onChange={handlePincodeChange}
//               maxLength={6}
//               aria-invalid={pinError ? "true" : "false"}
//               className={`w-full p-4 border-2 rounded-md focus:outline-none focus:ring-2 ${
//                 pinError
//                   ? "border-red-500 focus:ring-red-500"
//                   : "border-gray-300 focus:ring-blue-500"
//               }`}
//               placeholder="Enter Pincode"
//             />
//           </div>
//           {pinError && (
//             <p className="text-red-600 text-sm mb-4">{pinError}</p>
//           )}

//           <div className="flex justify-end space-x-4">
//             <button
//               onClick={async () => {
//                 if (pincode.length !== 6) {
//                   setPinError("Pincode must be exactly 6 digits.");
//                   return;
//                 }
//                 await fetchLatLngFromPincode();
//                 setDialogOpen(false);
//               }}
//               className="px-6 py-2 text-white bg-blue-600 hover:bg-blue-700 rounded-lg shadow-md transition duration-300 disabled:opacity-60"
//             >
//               Apply
//             </button>
//           </div>
//         </div>
//       </Dialog>
//     </div>
//   );
// };

// export default PincodeToCity;
